/** Turn [src:<id>] citation markers into numbered markdown links to the cited Source */

import type { AskCitation } from '@agentwiki/shared'

const MARKER = /\[src:([A-Za-z0-9_-]{1,64})\]/g

export interface CitationRender {
  markdown: string
  /** source ids in first-cited order; index + 1 is the citation number */
  order: string[]
}

/** Fenced blocks and inline code spans keep their markers verbatim, matching the API parser */
const CODE = /(^|\n)(```|~~~)[^\n]*\n[\s\S]*?(\n\2[^\n]*(?=\n|$)|$)|`[^`\n]*`/g

/** Apply `cite` to the text outside fenced blocks and inline code spans */
function outsideCode(content: string, cite: (text: string) => string): string {
  let markdown = ''
  let last = 0
  for (const match of content.matchAll(CODE)) {
    markdown += cite(content.slice(last, match.index)) + match[0]
    last = match.index + match[0].length
  }
  return markdown + cite(content.slice(last))
}

export function renderCitations(content: string, sourceHref: (id: string) => string): CitationRender {
  const order: string[] = []
  const markdown = outsideCode(content, (text) => text.replace(MARKER, (_, id: string) => {
    let n = order.indexOf(id) + 1
    if (n === 0) n = order.push(id)
    return `[[${n}]](${sourceHref(id)} "citation")`
  }))
  return { markdown, order }
}

const ANSWER_MARKER = /\[(wiki|src|doc):([A-Za-z0-9_-]{1,120})\]/g
const ANSWER_KIND = { wiki: 'wiki', src: 'source', doc: 'document' } as const

/**
 * Turn the [wiki:<slug>], [src:<id>], [doc:<slug>] markers of an Ask answer into links numbered like `citations`
 * (the API returns them in first-cited order). Markers the API did not return are dropped.
 */
export function renderAnswerCitations(answer: string, citations: AskCitation[]): string {
  return outsideCode(answer, (text) => text.replace(ANSWER_MARKER, (_, kind: keyof typeof ANSWER_KIND, ref: string) => {
    const n = citations.findIndex((c) => c.kind === ANSWER_KIND[kind] && c.ref === ref) + 1
    return n ? `[[${n}]](${citations[n - 1].path} "citation")` : ''
  }))
}

/** Only in-app Source links count as citations; agent-written links cannot pose as one */
export function isCitationHref(href: string | undefined): boolean {
  return !!href && /^\/p\/[^/]+\/sources\/[A-Za-z0-9_-]{1,64}$/.test(href)
}

/** Same anchor rule as the API TOC so heading ids match `toc[].anchor` */
export function headingAnchor(text: string): string {
  return text
    .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .slice(0, 80) || 'section'
}
